import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MessageService } from 'primeng/api';
import {
  CadArtifactRef,
  MouldingDownstreamExecution,
  MouldingNodeRun,
  MouldingStepParameterPriority,
  MouldingStepParameters,
} from '../../../models/moulding-item.model';
import { PullDirection } from '../../../models/pull-direction.model';
import { MouldingGraphService } from './moulding-graph.service';
import { MouldingItemService } from './moulding-item.service';
import {
  isNodeRunKind,
  isProcessingNodeKind,
  AsyncNodeKind,
  NodeRunKind,
  NodeRunStatus,
  ProcessingNodeKind,
} from '../nodes/moulding-node-registry';

/**
 * Coordinates Moulding processing steps around the page-scoped graph.
 *
 * It owns flow-specific processing state and invokes the item API. The backend
 * resolves and validates graph inputs; the graph service owns editor topology
 * and node runtime storage.
 */
@Injectable()
export class MouldingProcessingService {
  private static readonly downstreamExecutionTimeoutMs = 3 * 60 * 1000;

  private readonly graph = inject(MouldingGraphService);
  private readonly itemService = inject(MouldingItemService);
  private readonly messageService = inject(MessageService);

  private flowId: number | null = null;
  private currentFlowName = '';
  private stepParameterDefaults: Record<string, MouldingStepParameters> = {};
  private stepParameterDescriptions: Record<string, Record<string, string>> =
    {};
  private stepParameterPriorities: Record<
    string,
    Record<string, MouldingStepParameterPriority>
  > = {};
  private stepParameterUnits: Record<string, Record<string, string | null>> =
    {};
  private stepDescriptions: Record<string, string> = {};
  private downstreamExecutionTimeout: ReturnType<typeof setTimeout> | null =
    null;

  readonly runDownstreamEnabled = signal(true);
  readonly downstreamExecutionActive = signal(false);

  get flowName(): string {
    return this.currentFlowName;
  }

  connectToFlow(
    flowId: number,
    flowName: string,
    runDownstreamEnabled: boolean,
  ): void {
    this.flowId = flowId;
    this.currentFlowName = flowName;
    this.runDownstreamEnabled.set(runDownstreamEnabled);
  }

  setFlowName(flowName: string): void {
    this.currentFlowName = flowName;
  }

  disconnect(): void {
    this.clearDownstreamExecution();
    this.flowId = null;
    this.currentFlowName = '';
    this.stepParameterDefaults = {};
    this.stepParameterDescriptions = {};
    this.stepParameterPriorities = {};
    this.stepParameterUnits = {};
    this.stepDescriptions = {};
  }

  applyNodeRuns(nodeRuns: MouldingNodeRun[]): void {
    for (const nodeRun of nodeRuns) {
      this.applyNodeRun(nodeRun);
    }
  }

  setStepParameterDefinitions(
    defaults: Record<string, MouldingStepParameters>,
    descriptions: Record<string, Record<string, string>>,
    priorities: Record<string, Record<string, MouldingStepParameterPriority>>,
    units: Record<string, Record<string, string | null>>,
    stepDescriptions: Record<string, string> = {},
  ): void {
    this.stepParameterDefaults = defaults;
    this.stepParameterDescriptions = descriptions;
    this.stepParameterPriorities = priorities;
    this.stepParameterUnits = units;
    this.stepDescriptions = stepDescriptions;
  }

  getStepParameterDescriptions(
    kind: ProcessingNodeKind,
  ): Record<string, string> {
    return this.stepParameterDescriptions[kind] ?? {};
  }

  getStepParameterPriority(
    kind: ProcessingNodeKind,
    parameterName: string,
  ): MouldingStepParameterPriority {
    return this.stepParameterPriorities[kind]?.[parameterName] ?? 'low';
  }

  getStepParameterUnit(
    kind: ProcessingNodeKind,
    parameterName: string,
  ): string | null {
    return this.stepParameterUnits[kind]?.[parameterName] ?? null;
  }

  getStepDescription(kind: ProcessingNodeKind): string {
    return this.stepDescriptions[kind] ?? '';
  }

  getEffectiveStepParameters(
    nodeId: string,
    kind: ProcessingNodeKind,
  ): MouldingStepParameters {
    return {
      ...(this.stepParameterDefaults[kind] ?? {}),
      ...(this.graph.getNodeData(nodeId, kind)?.parameters ?? {}),
    };
  }

  canStartProcessingNode(nodeId: string, kind: AsyncNodeKind): boolean {
    const data = this.graph.getNodeData(nodeId, kind);
    return (
      this.flowId !== null &&
      !this.downstreamExecutionActive() &&
      data?.status !== 'pending' &&
      data?.status !== 'processing'
    );
  }

  async startProcessingNode(
    nodeId: string,
    kind: AsyncNodeKind,
    runDownstream = true,
  ): Promise<void> {
    if (this.flowId === null) {
      throw new Error('Save the flow before starting this node.');
    }
    const data = this.graph.getNodeData(nodeId, kind);
    if (
      this.downstreamExecutionActive() ||
      data?.status === 'pending' ||
      data?.status === 'processing'
    ) {
      return;
    }

    const previousStatus = data?.status ?? 'idle';
    this.updateNodeStatus(nodeId, kind, 'pending');
    try {
      const shouldRunDownstream =
        runDownstream && this.runDownstreamEnabled() && kind !== 'dfm_check';
      const nodeRun = await firstValueFrom(
        this.itemService.startNodeStep$(
          this.flowId,
          nodeId,
          this.graph.serialize(),
          undefined,
          shouldRunDownstream,
        ),
      );
      if (this.graph.getNodeData(nodeId, kind)?.status === 'pending') {
        this.applyNodeRun(nodeRun);
      }
      if (shouldRunDownstream) {
        this.setDownstreamExecutionActive();
      }
    } catch (error) {
      if (this.graph.getNodeData(nodeId, kind)?.status === 'pending') {
        this.updateNodeStatus(nodeId, kind, previousStatus);
      }
      throw error;
    }
  }

  async setPullDirection(
    nodeId: string,
    pullDirection: PullDirection,
  ): Promise<void> {
    if (this.flowId === null) {
      throw new Error('Save the flow before selecting a pull direction.');
    }
    if (this.downstreamExecutionActive()) {
      return;
    }

    const shouldRunDownstream = this.runDownstreamEnabled();
    const nodeRun = await firstValueFrom(
      this.itemService.setPullDirection$(
        this.flowId,
        nodeId,
        pullDirection,
        this.graph.serialize(),
        shouldRunDownstream,
      ),
    );
    this.applyNodeRun(nodeRun);
    if (shouldRunDownstream) {
      this.setDownstreamExecutionActive();
    }
  }

  async setCadInputNode(
    nodeId: string,
    artifact: CadArtifactRef,
  ): Promise<void> {
    if (this.flowId === null) {
      throw new Error('Save the flow before selecting CAD.');
    }
    if (this.downstreamExecutionActive()) {
      return;
    }

    const shouldRunDownstream = this.runDownstreamEnabled();
    const nodeRun = await firstValueFrom(
      this.itemService.startNodeStep$(
        this.flowId,
        nodeId,
        this.graph.serialize(),
        { cad: artifact },
        shouldRunDownstream,
      ),
    );
    this.applyNodeRun(nodeRun);
    if (shouldRunDownstream) {
      this.setDownstreamExecutionActive();
    }
  }

  applyDownstreamExecutionUpdate(
    downstreamExecution: MouldingDownstreamExecution,
  ): void {
    if (downstreamExecution.status === 'started') {
      if (!this.downstreamExecutionActive()) {
        this.setDownstreamExecutionActive();
      }
      return;
    }
    if (downstreamExecution.status === 'failed') {
      this.notifyDownstreamFailure(
        downstreamExecution.error_message ?? 'Downstream execution failed.',
      );
    }
    this.clearDownstreamExecution();
  }

  private applyNodeRun(nodeRun: MouldingNodeRun): void {
    const kind = this.getNodeKindForRunStepType(nodeRun.step_type);
    if (!kind || this.graph.getNodeKind(nodeRun.node_id) !== kind) {
      return;
    }

    const result =
      nodeRun.cad_file_id !== null && nodeRun.primary_cad_object_id !== null
        ? {
            artifact: {
              cad_file_id: nodeRun.cad_file_id,
              primary_cad_object_id: nodeRun.primary_cad_object_id,
              ...(nodeRun.pull_direction
                ? { pull_direction: nodeRun.pull_direction }
                : {}),
            },
            pull_directions: nodeRun.pull_directions,
            generated_files: nodeRun.generated_files,
            ...(nodeRun.dfm_checks.length > 0
              ? {
                  dfm_checks: nodeRun.dfm_checks,
                }
              : {}),
          }
        : null;
    const patch = {
      result,
      status: nodeRun.status,
      isOutdated: nodeRun.is_outdated,
      errorMessage: nodeRun.status === 'error' ? nodeRun.error_message : null,
    };
    if (isProcessingNodeKind(kind)) {
      this.graph.updateNodeData(nodeRun.node_id, kind, {
        ...patch,
        parameters: nodeRun.parameters,
      });
    } else {
      this.graph.updateNodeData(nodeRun.node_id, kind, patch);
    }
    if (this.downstreamExecutionActive()) {
      this.resetDownstreamExecutionTimeout();
    }
  }

  async applySocketNodeRun(nodeRun: MouldingNodeRun): Promise<void> {
    if (nodeRun.status !== 'complete') {
      this.applyNodeRun(nodeRun);
      return;
    }

    const flowId = this.flowId;
    if (flowId === null) {
      return;
    }
    try {
      const completedRun = await firstValueFrom(
        this.itemService.getNodeRun$(flowId, nodeRun.node_id),
      );
      if (this.flowId !== flowId) {
        return;
      }
      this.applyNodeRun(completedRun);
    } catch {
      // Preserve the terminal status even if the one-off result fetch fails.
      this.applyNodeRun(nodeRun);
    }
  }

  private updateNodeStatus(
    nodeId: string,
    kind: AsyncNodeKind,
    status: NodeRunStatus,
  ): void {
    const data = this.graph.getNodeData(nodeId, kind);
    if (!data) {
      return;
    }
    this.graph.updateNodeData(nodeId, kind, { status });
  }

  private setDownstreamExecutionActive(): void {
    this.downstreamExecutionActive.set(true);
    this.resetDownstreamExecutionTimeout();
    void this.graph.refreshAllNodes();
  }

  private clearDownstreamExecution(): void {
    if (this.downstreamExecutionTimeout !== null) {
      clearTimeout(this.downstreamExecutionTimeout);
      this.downstreamExecutionTimeout = null;
    }
    if (this.downstreamExecutionActive()) {
      this.downstreamExecutionActive.set(false);
      void this.graph.refreshAllNodes();
    }
  }

  private resetDownstreamExecutionTimeout(): void {
    if (this.downstreamExecutionTimeout !== null) {
      clearTimeout(this.downstreamExecutionTimeout);
    }
    this.downstreamExecutionTimeout = setTimeout(() => {
      this.notifyDownstreamFailure(
        'Downstream execution timed out waiting for progress.',
      );
      this.clearDownstreamExecution();
    }, MouldingProcessingService.downstreamExecutionTimeoutMs);
  }

  private notifyDownstreamFailure(detail: string): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Downstream execution failed',
      detail,
      life: 8000,
    });
  }

  private getNodeKindForRunStepType(stepType: string): NodeRunKind | null {
    return isNodeRunKind(stepType) ? stepType : null;
  }
}
